import { UploadArchiveResult } from '@/core/domain/archive/Archive/Archive.types';
import { uploadArchive } from '@/core/domain/archive/Archive/Archive.service';
import { Form, Modal, ModalProps, Upload, message } from 'antd';
import { FaImages } from 'react-icons/fa';
import {
  useWorkOrderChecklistImageCreate,
  useWorkOrderChecklistImageList,
  useWorkOrderChecklistImageRemove,
} from '@/core/domain/workorder/WorkOrdersChecklist/WorkOrdersChecklist.hooks';
import { useWorkorderChecklistContext } from '../WorkorderChecklistProvider';
import { WorkorderChecklistItemType } from '../WorkorderChecklistProvider/WorkorderChecklistProvider.types';
import { WorkOrderChecklistImagesFormValues } from '@/core/domain/workorder/WorkOrdersChecklist/WorkOrdersChecklist.types';
import { compressImage } from '@/utils/functions/compressImage';
import { validateImage } from '@/utils/functions/validateImage';
import type { UploadFile } from 'antd/lib/upload/interface';
import { UploadRequestOption } from 'rc-upload/lib/interface';
import { useRef } from 'react';

interface Props extends ModalProps {
  onCancel: () => void;
}

const MIME_TYPES = ['image/png', 'image/jpg', 'image/jpeg'];

const WorkorderChecklistImagesCreateModal: React.FC<Props> = (props) => {
  const [form] = Form.useForm<WorkOrderChecklistImagesFormValues>();
  const checklistImageCreate = useWorkOrderChecklistImageCreate();
  const removeImage = useWorkOrderChecklistImageRemove();
  const { checklistItemId, checklistItemType } = useWorkorderChecklistContext();

  const pendingFilePaths = useRef<string[]>([]);
  const uploadCountRef = useRef({ total: 0, completed: 0, failed: 0 });

  const { data } = useWorkOrderChecklistImageList({
    diagnosticId:
      checklistItemType == WorkorderChecklistItemType.Diagnostic
        ? checklistItemId
        : undefined,
    workOrderComplementId:
      checklistItemType == WorkorderChecklistItemType.Workorder
        ? checklistItemId
        : undefined,
  });

  function handleCancel() {
    form.resetFields();
    props.onCancel();
  }

  function findSavedImage(response?: UploadArchiveResult) {
    const images = data?.listChecklistImage ?? [];
    if (!response) return undefined;

    return (
      images.find((image) => image.filePath === response.filePath) ??
      images.find((image) => image.filePath?.endsWith(response.filePath)) ??
      images.find((image) => image.filePath?.endsWith(response.name))
    );
  }

  async function handleUpload({
    file,
    onSuccess,
    onError,
  }: UploadRequestOption) {
    try {
      message.destroy();

      const compressedFile = await compressImage(file as File);
      validateImage(compressedFile);

      const archiveResult = await uploadArchive({
        file: [compressedFile],
        isPublic: false,
      });

      pendingFilePaths.current.push(archiveResult.filePath);
      uploadCountRef.current.completed++;
      onSuccess?.(archiveResult);
    } catch (error) {
      uploadCountRef.current.failed++;
      onError?.(error as Error);
    } finally {
      const { total, completed, failed } = uploadCountRef.current;

      if (completed + failed === total && total > 0) {
        if (pendingFilePaths.current.length > 0) {
          try {
            await checklistImageCreate.mutateAsync({
              ...(checklistItemType === WorkorderChecklistItemType.Workorder
                ? { workOrderComplementId: checklistItemId }
                : { diagnosticId: checklistItemId }),
              filePath: pendingFilePaths.current,
            });
          } finally {
            pendingFilePaths.current = [];
            uploadCountRef.current = { total: 0, completed: 0, failed: 0 };
          }
        } else {
          uploadCountRef.current = { total: 0, completed: 0, failed: 0 };
        }
      }
    }
  }

  async function handleRemove(file: UploadFile) {
    const response = file.response as UploadArchiveResult | undefined;

    if (!response?.filePath) return true;

    const savedImage = findSavedImage(response);
    if (!savedImage) return true;

    try {
      await removeImage.mutateAsync(savedImage.id);
      return true;
    } catch {
      return false;
    }
  }

  return (
    <Modal
      {...props}
      title="Adicionar imagens"
      onCancel={handleCancel}
      okText="Concluir"
      onOk={handleCancel}
      cancelButtonProps={{ style: { display: 'none' } }}
      width={500}
      confirmLoading={checklistImageCreate.isLoading || removeImage.isLoading}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="images"
          label="Imagens"
          getValueFromEvent={(e) => {
            const files = Array.isArray(e) ? e : e && e.fileList;
            return files;
          }}
        >
          <Upload.Dragger
            multiple
            style={{ padding: 32 }}
            beforeUpload={(file, fileList) => {
              if (uploadCountRef.current.total === 0) {
                uploadCountRef.current.total = fileList.length;
              }

              if (!MIME_TYPES.includes(file.type)) {
                message.error('Tipo de arquivo inválido, permitido PNG e JPG');
                uploadCountRef.current.failed++;
                return Upload.LIST_IGNORE;
              }

              return true;
            }}
            customRequest={handleUpload}
            onRemove={handleRemove}
            listType="picture"
          >
            <FaImages size={32} />
            <p style={{ marginTop: 10 }}>Arraste ou selecione as imagens</p>
            <p>
              <small>As imagens são salvas automaticamente</small>
            </p>
          </Upload.Dragger>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default WorkorderChecklistImagesCreateModal;
